/** Fallback MediaRecorder timeslice when init does not return chunkDurationMs. */
export const DEFAULT_CHUNK_DURATION_MS = 15 * 1000

/**
 * Live chunk duration returned by recording/init (MediaRecorder timeslice). A closed or crashed tab loses whatever
 * hasn't uploaded yet, so this bounds that loss to about one chunk.
 */
export const INIT_CHUNK_DURATION_MS = 10 * 1000

/**
 * Bound wait after MediaRecorder.stop() for a late dataavailable (Safari).
 * Keeps flush from racing ahead of the last blob enqueue.
 */
export const RECORDER_STOP_DATA_WAIT_MS = 2000

/**
 * Longest one storage PUT of a chunk may take before it's abandoned and retried. Chunks upload one at a time
 * (INT-1061), so a request that never answers would otherwise hold every later chunk back. A 10s chunk at the
 * recording's highest bitrate is ~3.3 MB, which a 0.5 Mbps uplink still sends well inside this.
 */
export const CHUNK_PUT_TIMEOUT_MS = 60 * 1000

/** Upload statuses a retry can't fix: bad or mismatched session (400/409), rejected client token (401/403). */
export const NON_RETRYABLE_CHUNK_UPLOAD_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 409])

/**
 * Longest the end-of-interview flush waits for chunk uploads, so a candidate on a dead network isn't stuck on
 * the leave screen. Uploads still running carry on in the background and are reported in recording_flush.
 */
export const FLUSH_UPLOAD_WAIT_MS = 30 * 1000

/**
 * Grace after INTERVIEW_DONE / RESULT_COMPLETED during which wrap-up APIs
 * (part-url mint, session-log append) may still succeed. Covers an in-flight
 * chunk, the stop() flush chunk and their upload retries
 * (CHUNK_UPLOAD_RETRY_DELAYS_MS), so it is not tied to the chunk length.
 */
export const COMPLETED_WRAPUP_GRACE_MS = 90 * 1000

/**
 * Frame rate of the interview recording. The canvas compositor draws and captures at this rate, and the camera and
 * screen share ask for it too (`ideal`, so devices that can't still work) — so every source feeds the recording at a
 * steady 30fps instead of whatever the device defaults to.
 */
export const RECORDING_FRAME_RATE = 30

/**
 * Video bitrate cap for the interview recording. Without it Chrome encodes at ~2.5 Mbps, and each 30s chunk upload
 * fills a typical 1–3 Mbps home uplink, starving the live WebRTC interview audio (INT-1043). 800 kbps keeps a
 * 1280-wide screen share legible.
 */
export const RECORDER_VIDEO_BITS_PER_SECOND = 800_000

/** Recording audio bitrate when recording/init returns none (INT-1059 lowers it on slow connections). */
export const RECORDER_AUDIO_BITS_PER_SECOND = 128_000

/** Recording frame size when recording/init returns none (INT-1059 shrinks it on slow connections). */
export const RECORDER_VIDEO_WIDTH = 1280
export const RECORDER_VIDEO_HEIGHT = 720
